import React, { useEffect, useState, useRef } from 'react';
import format from 'date-fns/format';
import subDays from 'date-fns/subDays';
import startOfMonth from 'date-fns/startOfMonth';
import endOfMonth from 'date-fns/endOfMonth';
import startOfYear from 'date-fns/startOfYear';
import endOfYear from 'date-fns/endOfYear';
import coreSettings from '../../../helper/coreSetting';
import SarasPopup from '../../container/popup/SarasPopup';
import SarasTrans from '../../notifications/SarasTrans';
import './SarasDateQPMenu.css';
import { startOfDay, startOfWeek, endOfWeek, subWeeks } from 'date-fns';
import ActionButton from '../../input/buttons/button/SarasButton';

const quickRanges = [
  {
    label: 'Custom Range',
    shortLabel: 'Custom Range',
    getRange: () => ({
      startDate: undefined,
      endDate: undefined,
    }),
  },
  {
    label: 'Today',
    shortLabel: 'Today',
    getRange: () => {
      const today = startOfDay(new Date());
      return {
        startDate: today,
        endDate: today,
      };
    },
  },
  {
    label: 'Yesterday',
    shortLabel: 'Yest',
    getRange: () => {
      const yesterday = startOfDay(subDays(new Date(), 1));
      return {
        startDate: yesterday,
        endDate: yesterday,
      };
    },
  },
  {
    label: 'Last 7 Days',
    shortLabel: 'L7D',
    getRange: () => ({
      startDate: subDays(new Date(), 6),
      endDate: new Date(),
    }),
  },
  {
    label: 'This Week',
    shortLabel: 'TW',
    getRange: () => ({
      startDate: startOfWeek(new Date(), { weekStartsOn: 1 }), // Monday as first day
      endDate: endOfWeek(new Date(), { weekStartsOn: 1 }),     // Sunday as last day
    }),
  },
  {
    label: 'Last Week',
    shortLabel: 'LW',
    getRange: () => {
      // Get the start and end of the previous week (Monday to Sunday)
      const lastWeekStart = startOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 });
      const lastWeekEnd = endOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 });
      return {
        startDate: lastWeekStart,
        endDate: lastWeekEnd,
      };
    },
  },
  {
    label: 'This Month',
    shortLabel: 'TM',
    getRange: () => ({
      startDate: startOfMonth(new Date()),
      endDate: endOfMonth(new Date()),
    }),
  },
  {
    label: 'Last Month',
    shortLabel: 'LM',
    getRange: () => {
      const now = new Date();
      const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      return {
        startDate: startOfMonth(prevMonth),
        endDate: endOfMonth(prevMonth),
      };
    },
  },
  {
    label: 'YTD',
    shortLabel: 'YTD',
    getRange: () => ({
      startDate: startOfYear(new Date()),
      endDate: endOfMonth(new Date()),
    }),
  },
  {
    label: 'This Year',
    shortLabel: 'TY',
    getRange: () => ({
      startDate: startOfYear(new Date()),
      endDate: endOfYear(new Date()),
    }),
  },
  {
    label: 'Last Year',
    shortLabel: 'LY',
    getRange: () => {
      const now = new Date();
      const prevYear = now.getFullYear() - 1;
      return {
        startDate: new Date(prevYear, 0, 1), // January 1st of previous year
        endDate: new Date(prevYear, 11, 31), // December 31st of previous year
      };
    },
  },
];

const quickRangeGroups = [
  { label: 'Year', ranges: ['YTD', 'This Year', 'Last Year'] },
  { label: 'Month', ranges: ['This Month', 'Last Month'] },
  { label: 'Week', ranges: ['Last 7 Days', 'This Week', 'Last Week'] },
  { label: 'Recent', ranges: ['Today', 'Yesterday'] },
].map(group => ({
  ...group,
  ranges: group.ranges.map(label => quickRanges.find(range => range.label === label)),
}));

const SarasDateQPMenu = ({ value, hideStarIcon, buttonClassName, 
    titleClassName, onChange, onlyIcon, deviceInfo, allowCustomRange = false }) => {
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState(
    value || {
      startDate: null,
      endDate: null,
    }
  );

  const rangePickerRef = useRef();

  const synchDateCriteria = () => {
      if(value) {
        setRange(value);
      }
  };

  useEffect(synchDateCriteria, [value]);

  const handleQuickSelect = (quickRange) => {
    const newRange = quickRange.getRange();
    setOpen(false);

    if(newRange.startDate && newRange.endDate) {
      setRange(newRange);
      if (onChange) onChange(rangePickerRef, newRange, false);
    }
    else {
      if (onChange) onChange(rangePickerRef, newRange, true);
    }
  };

  const getMatchingQuickRange = (currentRange) => {
    if (!currentRange.startDate || !currentRange.endDate) {
      return null;
    }

    // Compare dates (ignore time)
    const currentStart = format(currentRange.startDate, 'yyyy-MM-dd');
    const currentEnd = format(currentRange.endDate, 'yyyy-MM-dd');

    for (const quickRange of quickRanges) {
      if (quickRange.label === 'Custom Range') continue;
      
      const quickRangeResult = quickRange.getRange();
      
      if (quickRangeResult.startDate && quickRangeResult.endDate) {
        const quickStart = format(quickRangeResult.startDate, 'yyyy-MM-dd');
        const quickEnd = format(quickRangeResult.endDate, 'yyyy-MM-dd');
        
        if (currentStart === quickStart && currentEnd === quickEnd) {
          return quickRange;
        }
      }
    }
    
    return null;
  };

  // Update the label logic
  const matchingRange = getMatchingQuickRange(range);
  const matchingLabel = matchingRange && (deviceInfo && deviceInfo.isTabletWidthOrLess
    ? matchingRange.shortLabel : matchingRange.label);
  const label = range.startDate 
    ? (matchingLabel || `${format(range.startDate, coreSettings.currentDateLocale)} to ${format(range.endDate, coreSettings.currentDateLocale)}`)
    : "Select Date Range";

// console.log("selected date range:", range);
  // const label = range.startDate ? `${format(range.startDate, coreSettings.currentDateLocale)} to ${format(range.endDate, coreSettings.currentDateLocale)}` : "Select Date Range";

  return (
    <>
      <div
        ref={rangePickerRef}
        tabIndex={-1}
        >
        <ActionButton
          onClick={() => setOpen(previous => !previous)}
          className={`${buttonClassName || 'uiDateRangeTrigger uiHover uiFlexBox3'}`}
          starIcon={hideStarIcon ? "" : "daterange"}
          onlyIcon={onlyIcon} // Hide text on mobile
          starIconClassName={hideStarIcon ? "" : "uiBrandColor1"}
          endIcon="arrowmenu"
          endIconClassName="uiBrandColor1"
        >
          <span className={titleClassName || ''}>{label}</span>
        </ActionButton>
      </div>
      <SarasPopup anchorRef={rangePickerRef} open={open} onClose={() => setOpen(false)}>
        <div
          className="uiDateQPPanel"
          role="dialog"
          aria-label="Select Date Range"
          onKeyDown={event => {
            if (event.key === 'Escape') {
              event.stopPropagation();
              setOpen(false);
            }
          }}
        >
          {quickRangeGroups.map(group => (
            <section className="uiDateQPGroup" key={group.label} aria-label={group.label}>
              <div className="uiDateQPHeading"><SarasTrans value={group.label} /></div>
              <div className="uiDateQPCards">
                {group.ranges.map(quickRange => (
                  <button
                    key={quickRange.label}
                    type="button"
                    autoFocus={quickRange === quickRangeGroups[0].ranges[0]}
                    className={`uiDateQPCard uiTabButtonLook${matchingRange === quickRange ? ' uiTabActiveButtonLook' : ''}`}
                    aria-pressed={matchingRange === quickRange}
                    onClick={() => handleQuickSelect(quickRange)}
                  >
                    <SarasTrans value={quickRange.label} />
                  </button>
                ))}
              </div>
            </section>
          ))}
          {allowCustomRange && (
            <div className="uiDateQPCustom">
              {/* The host owns the custom date inputs and receives the existing anchor. */}
              <ActionButton
                onClick={() => handleQuickSelect(quickRanges[0])}
                className='uiButtonAppText2 uiDateQPCard uiBrandColor1'
              >
                <SarasTrans value="Provide Custom Range" />
              </ActionButton>
            </div>
          )}
        </div>
      </SarasPopup>
    </>
  );
};

export default SarasDateQPMenu;
